/* eslint-disable no-unused-vars */
import { X } from 'lucide-react';
import React from 'react';
import { createOrganization } from '../../api';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useSession } from '../../contexts/SessionContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../../hooks/useActionDialog';

type OrganizationDialogProps = {
  actionDialogReturn: UseActionDialogReturn;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function OrganizationDialog({ actionDialogReturn, onNavigate }: OrganizationDialogProps) {
  const {
    actionDialog,
    dialogBusy,
    dialogError,
    setActionDialog,
    setDialogBusy,
    setDialogError,
  } = actionDialogReturn;

  const { session } = useSession();
  const { toast } = useFeedback();
  const { setActiveOrgId, setWorkspaceVersion } = useWorkspace();

  if (!actionDialog || actionDialog.kind !== 'organization') return null;

  const close = () => { if (!dialogBusy) setActionDialog(null); };

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session || !actionDialog || actionDialog.kind !== 'organization' || dialogBusy) return;
    setDialogBusy(true);
    setDialogError('');
    try {
      const organization = await createOrganization(session.accessToken, {
        name: actionDialog.name.trim()
      });
      setActiveOrgId(organization.tenantOrganizationId);
      setWorkspaceVersion((v) => v + 1);
      toast('success', 'Workspace created');
      setActionDialog(null);
      onNavigate('/onboarding', { replace: true });
    } catch (err: any) {
      setDialogError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setDialogBusy(false);
    }
  }

  return (
    <div className="dialogOverlay" role="presentation" onClick={close}>
      <form className="dialogPanel" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="dialogHeader">
          <div>
            <div className="panelKicker">Workspace</div>
            <h3>New workspace</h3>
            <p>Create a shared home for projects, releases, and access.</p>
          </div>
          <button type="button" className="iconButton" onClick={close} aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>
        <div className="settingsForm">
          <label className="settingsField">
            <span>Name</span>
            <input
              value={actionDialog.name}
              onChange={(e) => setActionDialog({ ...actionDialog, name: e.target.value })}
              placeholder="Acme Labs"
            />
          </label>
          <p className="fieldHint">We&apos;ll generate a unique technical name for this workspace automatically.</p>
        </div>
        {dialogError && <p className="fieldError">{dialogError}</p>}
        <div className="dialogActions">
          <button type="button" className="secondaryButton" onClick={close} disabled={dialogBusy}>Cancel</button>
          <button type="submit" className="primaryButton" disabled={dialogBusy}>
            {dialogBusy ? 'Creating…' : 'Create workspace'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { OrganizationDialog };
